import React from 'react';
import { Link } from 'react-router-dom';
import { FileText } from 'lucide-react';
import SEO from '../components/SEO';

const Terms = () => {
  return (
    <div className="min-h-screen bg-[#111216]">
      <SEO 
        title="Terms of Service | Detailing Disciples"
        description="Detailing Disciples terms of service and service policies."
        canonical="https://thedetailingdisciples.com/terms"
      />

      <section className="pt-32 pb-16 bg-gradient-to-b from-[#1A1A1F] to-[#111216]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-center mb-6">
              <FileText className="w-12 h-12 text-[#B8860B]" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white text-center mb-4">
              Terms of Service
            </h1>
            <p className="text-gray-400 text-center mb-8">
              Last Updated: December 2025
            </p>
            <div className="bg-yellow-900/20 border border-yellow-900/40 rounded-xl p-6 mb-8">
              <p className="text-yellow-200 text-sm">
                ⚠️ <strong>OWNER ACTION REQUIRED:</strong> These terms must be reviewed by the business owner and/or legal counsel before publication. Customize all sections to reflect actual business policies.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-16 bg-[#111216]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white/5 border border-white/10 rounded-2xl p-8 md:p-12 space-y-8 text-gray-300">
            
            <div>
              <h2 className="text-2xl font-bold text-white mb-4">1. Service Agreement</h2>
              <p>
                By booking a service with Detailing Disciples, you agree to these terms. All services are provided by owner-operated personnel with care and attention to your vehicle.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-4">2. Estimates and Final Pricing</h2>
              <p className="mb-4">
                Quotes provided are estimates based on the information and photos you provide. Final pricing may vary based on:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Actual vehicle size and condition upon inspection</li>
                <li>Additional services requested during the appointment</li>
                <li>Unexpected conditions discovered during service</li>
              </ul>
              <p className="mt-4">
                You will be notified of any significant price changes before work proceeds.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-4">3. Deposits and Payment</h2>
              <p>
                Some services may require a deposit at the time of booking. Accepted payment methods and deposit requirements will be communicated during the booking process. Full payment is due upon completion unless otherwise arranged.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-4">4. Scheduling and Cancellations</h2>
              <p className="mb-4">
                <strong className="text-white">Cancellation Policy:</strong> Please provide at least 24 hours notice if you need to cancel or reschedule. Cancellations with less than 24 hours notice may be subject to a cancellation fee.
              </p>
              <p>
                <strong className="text-white">Late Arrivals:</strong> If you are not present at the scheduled service location (for mobile service), we may need to reschedule and a trip fee may apply.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-4">5. Vehicle Access and Safety</h2>
              <p className="mb-4">
                For mobile service, you must provide:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Access to a water source and electrical outlet (if needed)</li>
                <li>Safe, level parking area</li>
                <li>Vehicle keys or access as required</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-4">6. Pre-Existing Damage</h2>
              <p>
                We inspect vehicles before service and document any pre-existing damage. We are not responsible for:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mt-4">
                <li>Pre-existing paint defects, chips, or scratches</li>
                <li>Fragile trim, badges, or aftermarket modifications</li>
                <li>Clear coat failure or paint that is already compromised</li>
              </ul>
              <p className="mt-4">
                Any concerns will be discussed before work begins.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-4">7. Personal Property</h2>
              <p>
                Please remove all valuables and personal items from your vehicle before service. Detailing Disciples is not responsible for lost or damaged personal property left in the vehicle.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-4">8. Service Limitations</h2>
              <p className="mb-4">
                <strong className="text-white">Paint Correction:</strong> We cannot guarantee removal of every defect. Deep scratches, rock chips, and damage below the clear coat cannot be corrected through polishing and may require body shop repair.
              </p>
              <p>
                <strong className="text-white">Ceramic Coating:</strong> Coatings enhance gloss and provide hydrophobic properties but do not make vehicles scratch-proof or maintenance-free. Regular washing is still required.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-4">9. Ceramic Coating Maintenance and Warranty</h2>
              <p>
                Ceramic coating packages may include product-specific warranty terms that require:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mt-4">
                <li>Proper curing period (no washing or rain exposure for 24-48 hours)</li>
                <li>Maintenance washing with pH-neutral products</li>
                <li>Periodic inspections or maintenance treatments</li>
              </ul>
              <p className="mt-4">
                Warranty details will be provided in writing before application.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-4">10. Customer-Provided Information</h2>
              <p>
                You are responsible for providing accurate information about your vehicle's condition, any known issues, and special instructions. Failure to disclose relevant information may affect service results and pricing.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-4">11. Satisfaction and Concerns</h2>
              <p>
                If you have any concerns about the completed service, please contact us within 48 hours so we can inspect the vehicle and address the issue. We stand behind our work and will make reasonable efforts to resolve any legitimate workmanship concerns.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-4">12. Contact Information</h2>
              <p>
                For questions about these terms or to discuss service policies:
              </p>
              <p className="mt-4">
                <strong className="text-white">Detailing Disciples</strong><br />
                Email: <a href="mailto:info@detailingdisciples.com" className="text-[#B8860B] hover:underline">info@detailingdisciples.com</a><br />
                Phone: <a href="tel:+17473207174" className="text-[#B8860B] hover:underline">(747) 320-7174</a>
              </p>
            </div>

          </div>
        </div>
      </section>

      <section className="py-8 bg-[#1A1A1F]">
        <div className="container mx-auto px-4 text-center">
          <Link to="/" className="text-[#B8860B] hover:text-[#C8960B] font-semibold">
            ← Back to Home
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Terms;